import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { selectActiveMedia } from 'store/selectors'
import { VISUAL_MEDIA_TYPE_OVERLAY } from 'containers/SubSidebar/data'
import EditIcon from 'assets/svgr-icons/Edit.svg'
import { useResponsive } from 'hooks/useResponsive'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { editorThunks } from 'store/features/editor/thunks'
import { TRotationDirection } from './types'
import RotateIcon from 'assets/svgr-icons/Rotate.svg'
import LayerOrderMenuIcon from 'assets/svgr-icons/Layer_order.svg'
import FitIcon from 'assets/svgr-icons/Fit.svg'
import FillIcon from 'assets/svgr-icons/Fill.svg'
import FlipIcon from 'assets/svgr-icons/Flip_horizontal.svg'
import { RotateRightIcon } from './styled'
import { T } from 'consts/translationKeys'
import { dispatch } from 'store'
import { projectDataThunks } from 'store/features/projectData/thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import { calculateMediaRect } from 'utils/media'
import { PROJECT_MEDIA_TYPES } from 'consts'
import {
  FILL,
  FIT,
  NONE,
  ROTATION_FULL_CIRCLE,
  ROTATION_STEP
} from 'consts/canvas'
import { TScaleMode } from 'types'
import { IMediaItem } from 'types/media'

const getIntrinsicMediaDimensions = (item: IMediaItem) => {
  const ow = item.meta.originalWidth ?? 0
  const oh = item.meta.originalHeight ?? 0
  if (ow > 0 && oh > 0) {
    return { width: ow, height: oh }
  }
  return { width: item.width, height: item.height }
}

interface IMediaControl {
  id: string
  tooltipText?: T
  icon?: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void
  type?: 'divider'
  disabled?: boolean
}

interface IUseMediaControlsParams {
  onOpenZOrderMenuFromToolbar?: (position: { x: number; y: number }) => void
}

export const useMediaControls = (params: IUseMediaControlsParams = {}) => {
  const { onOpenZOrderMenuFromToolbar } = params
  const activeMedia = useSelector(selectActiveMedia)
  const isMobileLayout = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)
  const projectDefaultDimensions = useSelector(
    projectDataSelectors.defaultDimensions
  )

  const handleTextEdit = useCallback(() => {
    if (!activeMedia) return

    dispatch(editorThunks.setIsMobileSubSidebarOpen(true))

    if (activeMedia.id && activeMedia.type) {
      dispatch(
        editorThunks.selectSingleTimelineItem({
          id: activeMedia.id,
          type: activeMedia.type
        })
      )
      const overlayType = VISUAL_MEDIA_TYPE_OVERLAY[activeMedia.type]
      if (overlayType) {
        dispatch(editorThunks.setAsideOverlayContent(overlayType))
      }
    }
  }, [activeMedia])

  const handleScale = (mode: TScaleMode = FILL) => {
    if (!activeMedia) return

    const { width: mediaWidth, height: mediaHeight } =
      getIntrinsicMediaDimensions(activeMedia)
    if (mediaWidth <= 0 || mediaHeight <= 0) return

    const { width, height, x, y } = calculateMediaRect({
      mediaDimensions: { width: mediaWidth, height: mediaHeight },
      mode,
      canvasDimensions: projectDefaultDimensions,
      rotation: activeMedia.rotation
    })

    dispatch(
      projectDataThunks.updateMediaItem({
        id: activeMedia.id,
        updates: { width, height, coordinates: { x, y } }
      })
    )
  }

  const handleHorizontalFlip = () => {
    if (!activeMedia) return
    const currentFlip = activeMedia.flip || { x: false, y: false }

    dispatch(
      projectDataThunks.updateMediaItem({
        id: activeMedia.id,
        updates: { flip: { x: !currentFlip.x, y: false } }
      })
    )
  }

  const handleRotate = (direction: TRotationDirection) => {
    if (!activeMedia) return

    const { width: mediaWidth, height: mediaHeight } =
      getIntrinsicMediaDimensions(activeMedia)
    if (mediaWidth <= 0 || mediaHeight <= 0) return

    const currentRotation = activeMedia.rotation || 0
    const newRotation =
      direction === 'right'
        ? (currentRotation + ROTATION_STEP) % ROTATION_FULL_CIRCLE
        : (currentRotation - ROTATION_STEP + ROTATION_FULL_CIRCLE) %
          ROTATION_FULL_CIRCLE
    const fitMode = calculateMediaRect({
      mediaDimensions: { width: mediaWidth, height: mediaHeight },
      mode: 'fit',
      canvasDimensions: projectDefaultDimensions,
      rotation: activeMedia.rotation
    })
    const fillMode = calculateMediaRect({
      mediaDimensions: { width: mediaWidth, height: mediaHeight },
      mode: 'fill',
      canvasDimensions: projectDefaultDimensions,
      rotation: activeMedia.rotation
    })

    const isFitMode =
      fitMode.width === activeMedia.width &&
      fitMode.height === activeMedia.height
    const isFillMode =
      fillMode.width === activeMedia.width &&
      fillMode.height === activeMedia.height

    const currentMode = isFitMode ? FIT : isFillMode ? FILL : NONE

    if (currentMode === NONE) {
      dispatch(
        projectDataThunks.updateMediaItem({
          id: activeMedia.id,
          updates: {
            rotation: newRotation
          }
        })
      )
      return
    }

    const { x, y, width, height } = calculateMediaRect({
      mediaDimensions: {
        width: mediaWidth,
        height: mediaHeight
      },
      mode: currentMode,
      canvasDimensions: projectDefaultDimensions,
      rotation: newRotation
    })

    dispatch(
      projectDataThunks.updateMediaItem({
        id: activeMedia.id,
        updates: {
          rotation: newRotation,
          width,
          height,
          coordinates: { x, y }
        }
      })
    )
  }

  const rotateLeftControl: IMediaControl = {
    id: 'rotate-left',
    tooltipText: T.ROTATE_LEFT_LABEL,
    icon: <RotateIcon />,
    onClick: () => handleRotate('left')
  }
  const rotateRightControl: IMediaControl = {
    id: 'rotate-right',
    tooltipText: T.ROTATE_RIGHT_LABEL,
    icon: <RotateRightIcon />,
    onClick: () => handleRotate('right')
  }

  const layerOrderControl: IMediaControl = {
    id: 'layer-order',
    tooltipText: T.LAYER_ORDER_MENU_LABEL,
    icon: <LayerOrderMenuIcon />,
    onClick: e => {
      if (!onOpenZOrderMenuFromToolbar) return
      const r = e.currentTarget.getBoundingClientRect()
      onOpenZOrderMenuFromToolbar({
        x: r.left,
        y: r.bottom + 4
      })
    }
  }

  const editTextControl: IMediaControl = {
    id: 'edit',
    tooltipText: T.EDIT_LABEL,
    icon: <EditIcon />,
    onClick: () => handleTextEdit()
  }

  const mediaControls: IMediaControl[] =
    activeMedia?.type === PROJECT_MEDIA_TYPES.text
      ? [
          ...(isMobileLayout ? [editTextControl] : []),
          rotateLeftControl,
          rotateRightControl,
          { id: 'divider', type: 'divider' },
          layerOrderControl
        ]
      : [
          rotateLeftControl,
          rotateRightControl,
          {
            id: 'divider',
            type: 'divider'
          },
          {
            id: 'fit',
            tooltipText: T.FIT_LABEL,
            icon: <FitIcon />,
            onClick: () => handleScale(FIT)
          },
          {
            id: 'fill',
            tooltipText: T.FILL_LABEL,
            icon: <FillIcon />,
            onClick: () => handleScale(FILL)
          },
          {
            id: 'flip-horizontal',
            tooltipText: T.FLIP_HORIZONTAL_LABEL,
            icon: <FlipIcon />,
            onClick: handleHorizontalFlip
          },
          { id: 'divider-end', type: 'divider' },
          layerOrderControl
        ]

  return { mediaControls }
}
